/**
 * MaskedOverlay - Privacy masking system for co-viewing sessions.
 * 
 * When activated, applies CSS-based masking to all elements tagged with
 * data-sensitive attributes. The user can toggle a preview to see exactly
 * what the support staff will see.
 * 
 * Usage:
 *   Wrap your app content:
 *   <MaskedOverlayProvider active={isMasked}>
 *     <Dashboard />
 *   </MaskedOverlayProvider>
 *
 *   Tag sensitive elements:
 *   <span data-sensitive="monetary">$12,345.67</span>
 *   <span data-sensitive="asset-name">Apple Inc.</span>
 *   <span data-sensitive="identifier">john@email.com</span>
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const MaskedOverlayContext = createContext({
  isMasked: false,
  isPreviewing: false,
  togglePreview: () => {},
});

export const useMaskedOverlay = () => useContext(MaskedOverlayContext);

/* ── CSS injected when mask is active ── */
const MASK_STYLES = `
/* Monetary values: blur + placeholder */
[data-masked-active="true"] [data-sensitive="monetary"],
[data-masked-active="true"] [data-sensitive="currency"] {
  color: transparent !important;
  text-shadow: none !important;
  background: repeating-linear-gradient(
    90deg,
    #94a3b8 0px, #94a3b8 6px,
    transparent 6px, transparent 8px
  );
  background-size: 100% 60%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 2px;
  user-select: none;
  min-width: 48px;
  display: inline-block;
}

/* Asset names: partial mask */
[data-masked-active="true"] [data-sensitive="asset-name"] {
  color: transparent !important;
  text-shadow: none !important;
  background: linear-gradient(90deg, #64748b 0%, #64748b 35%, transparent 35%, transparent 40%, #94a3b8 40%, #94a3b8 100%);
  background-size: 100% 60%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 2px;
  user-select: none;
  min-width: 64px;
  display: inline-block;
}

/* Identifiers (emails, account numbers): full concealment */
[data-masked-active="true"] [data-sensitive="identifier"],
[data-masked-active="true"] [data-sensitive="email"],
[data-masked-active="true"] [data-sensitive="account"] {
  color: transparent !important;
  text-shadow: none !important;
  background: #cbd5e1;
  border-radius: 2px;
  user-select: none;
  min-width: 80px;
  display: inline-block;
}

/* Rates/percentages: concealed */
[data-masked-active="true"] [data-sensitive="rate"] {
  color: transparent !important;
  text-shadow: none !important;
  background: repeating-linear-gradient(
    90deg,
    #94a3b8 0px, #94a3b8 4px,
    transparent 4px, transparent 6px
  );
  background-size: 100% 60%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 2px;
  user-select: none;
  min-width: 32px;
  display: inline-block;
}

/* Charts and graphs: blur */
[data-masked-active="true"] [data-sensitive="chart"],
[data-masked-active="true"] canvas,
[data-masked-active="true"] svg[data-sensitive] {
  filter: blur(8px) !important;
  pointer-events: none;
}

/* Generic sensitive catch-all */
[data-masked-active="true"] [data-sensitive="true"],
[data-masked-active="true"] [data-sensitive="generic"] {
  color: transparent !important;
  background: #e2e8f0;
  border-radius: 2px;
  user-select: none;
}

/* Ensure structural elements remain visible */
[data-masked-active="true"] [data-sensitive-label],
[data-masked-active="true"] label,
[data-masked-active="true"] th,
[data-masked-active="true"] [data-testid] {
  color: inherit !important;
  background: inherit !important;
}
`;

export function MaskedOverlayProvider({ active, children }) {
  const [isPreviewing, setIsPreviewing] = useState(false);
  const shouldMask = active && isPreviewing;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let styleEl = document.getElementById('masked-overlay-styles');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'masked-overlay-styles';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = MASK_STYLES;
    return () => { const el = document.getElementById('masked-overlay-styles'); if (el) el.remove(); };
  }, []);

  const togglePreview = useCallback(() => setIsPreviewing(p => !p), []);
  const contextValue = useMemo(() => ({ isMasked: active, isPreviewing, togglePreview }), [active, isPreviewing, togglePreview]);

  return (
    <MaskedOverlayContext.Provider value={contextValue}>
      <div data-masked-active={shouldMask ? "true" : "false"}>
        {children}
      </div>
    </MaskedOverlayContext.Provider>
  );
}

export default MaskedOverlayProvider;
